import React, { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { saveSFQQuestions } from "../Redux/feasibility";
import TemplatePicker from "../TemplatePicker";
import QuestionRowsEditor, {
  toEditorRows, toQuestionPayload, applyOverwrite, applyAppend,
  findInvalidRow, invalidRowMessage,
} from "../QuestionRowsEditor";

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$primary ? "#0d6efd" : "#dee2e6")};
  background: ${(p) => (p.$primary ? "#0d6efd" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#495057")};
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.9); }
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

export default function SFQQuestionEditor({ sfqId, editable, onDirtyChange }) {
  const dispatch = useDispatch();
  const sfqQuestions = useSelector((s) => s.feasibility.sfqQuestions);

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    setRows(toEditorRows(sfqQuestions));
    setDirty(false);
  }, [sfqQuestions]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // 브라우저 새로고침·탭 닫기 가드
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // 인앱 뒤로가기·메뉴 이동 가드
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("저장하지 않은 변경 사항이 있습니다.\n페이지를 이동하면 변경 사항이 사라집니다.\n이동하시겠습니까?")) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  function handleRowsChange(next) {
    setRows(next);
    setDirty(true);
  }

  // The picker hands back editor-shaped rows from a template or a previous version.
  function handleApply({ questions }, mode) {
    setRows(mode === "append" ? applyAppend(rows, questions) : applyOverwrite(questions));
    setDirty(true);
  }

  async function handleSave() {
    const invalid = findInvalidRow(rows);
    if (invalid) {
      alert(invalidRowMessage(invalid));
      return;
    }
    setSaving(true);
    try {
      await dispatch(saveSFQQuestions({ id: sfqId, questions: toQuestionPayload(rows) })).unwrap();
      setDirty(false);
    } catch (err) {
      // The rejection carried the reason nowhere — the rows stayed dirty and the save
      // looked like it had simply done nothing. The thunk hands back the server's
      // message (an over-long field, a frozen status), so say what went wrong.
      alert("저장 실패: " + (err?.message || "잠시 후 다시 시도해 주세요."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {editable && (
        <Toolbar>
          <Btn onClick={() => setPicking(true)}>템플릿 불러오기</Btn>
          <Btn $primary disabled={!dirty || saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save Questions"}
          </Btn>
          {!dirty && rows.length > 0 && (
            <span style={{ fontSize: 12, color: "#6c757d" }}>Saved</span>
          )}
        </Toolbar>
      )}

      {rows.length === 0 ? (
        <Empty>
          {editable
            ? "문항이 없습니다. '템플릿 불러오기'로 시작하거나 직접 추가하세요."
            : "문항이 없습니다."}
        </Empty>
      ) : null}

      <QuestionRowsEditor rows={rows} onChange={handleRowsChange} editable={editable} />

      {picking && (
        <TemplatePicker
          kind="questions"
          hasContent={rows.length > 0}
          onApply={handleApply}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
